/**
 * Home addition size buckets (Phase 12). Same pattern as
 * kitchen-size-ranges.ts/basement-size-ranges.ts: the addition brief asks
 * for "approximate square footage" rather than a bare numeric input,
 * structured so the engine only ever sees a resolved `room_size` sqft
 * value, never a bucket id - a bucket can be swapped or supplemented with
 * an exact figure without any engine change.
 *
 * Bucket boundaries are centered on a typical single-storey addition
 * (roughly 300-600 sq. ft. for a family room/primary suite/mudroom
 * addition) but extend well past that for a second-storey or multi-room
 * addition, matching the existing `DEFAULT_ROOM_SQFT.addition` planning
 * default of 400 sqft (see
 * packages/estimator-app/src/lib/defaults.ts) so the middle-low bucket
 * lines up with what the UI already assumed before this phase.
 */
export interface AdditionSizeRange {
  id: string;
  label: string;
  /** Representative square footage used to drive the calculation when this bucket is picked. */
  representativeSqft: number;
  min: number;
  /** null = no upper bound ("900+ sq. ft."). */
  max: number | null;
}

export const ADDITION_SIZE_RANGES: AdditionSizeRange[] = [
  { id: "under_200", label: "Under 200 sq. ft.", representativeSqft: 150, min: 0, max: 200 },
  { id: "200_400", label: "200–400 sq. ft.", representativeSqft: 300, min: 200, max: 400 },
  { id: "400_600", label: "400–600 sq. ft.", representativeSqft: 500, min: 400, max: 600 },
  { id: "600_900", label: "600–900 sq. ft.", representativeSqft: 750, min: 600, max: 900 },
  { id: "900_plus", label: "900+ sq. ft.", representativeSqft: 1200, min: 900, max: null }
];

export function findAdditionSizeRange(id: string | undefined): AdditionSizeRange | undefined {
  return ADDITION_SIZE_RANGES.find((r) => r.id === id);
}

/** Finds the bucket a given exact square footage falls into, for keeping the bucket answer and an exact value in sync. */
export function additionSizeRangeForSqft(sqft: number): AdditionSizeRange | undefined {
  return (
    ADDITION_SIZE_RANGES.find((r) => sqft >= r.min && (r.max === null || sqft < r.max)) ??
    ADDITION_SIZE_RANGES[ADDITION_SIZE_RANGES.length - 1]
  );
}
